import { UserCheck, Building2, ShieldCheck, PhoneCall } from "lucide-react";
import { useEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import AnimatedHeadline from "@/components/AnimatedHeadline";

const differentials = [
  {
    icon: UserCheck,
    title: "Atención personalizada",
    desc: "Cada cliente es único. Te asignamos un asesor dedicado que conoce tu situación a fondo y te acompaña en cada decisión.",
  },
  {
    icon: Building2,
    title: "Empresas y autónomos",
    desc: "Soluciones adaptadas a cualquier tipo de actividad, tamaño o sector. Da igual si empiezas o si ya facturas.",
  },
  {
    icon: ShieldCheck,
    title: "Asesoramiento integral",
    desc: "Fiscal, laboral, contable y jurídico en un solo lugar. Sin tener que ir de un despacho a otro.",
  },
  {
    icon: PhoneCall,
    title: "Contacto directo",
    desc: "Sin intermediarios ni esperas. Llámanos, escríbenos o pásate por la oficina.",
  },
];

export default function Trust() {
  const sectionRef  = useRef(null);
  const labelRef    = useRef(null);
  const goldLineRef = useRef(null);
  const cardsRef    = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Label
      gsap.fromTo(labelRef.current,
        { opacity: 0, y: 20 },
        { opacity: 1, y: 0, duration: 0.6, ease: "power2.out",
          scrollTrigger: { trigger: labelRef.current, start: "top 87%" } });

      // Gold line draw
      gsap.fromTo(goldLineRef.current,
        { scaleX: 0, transformOrigin: "center center" },
        { scaleX: 1, duration: 0.9, ease: "power2.out",
          scrollTrigger: { trigger: goldLineRef.current, start: "top 85%" } });

      // Cards — stagger with landing rotation
      if (cardsRef.current) {
        gsap.fromTo(Array.from(cardsRef.current.children),
          { opacity: 0, y: 70, rotation: 3, filter: "blur(4px)" },
          {
            opacity: 1, y: 0, rotation: 0, filter: "blur(0px)",
            stagger: 0.13, duration: 0.9, ease: "power3.out",
            scrollTrigger: { trigger: cardsRef.current, start: "top 85%" },
          });
      }
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={sectionRef}
      data-testid="trust-section"
      className="py-28 md:py-36 bg-[#f8f6f3]">

      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
        {/* Header */}
        <div className="text-center mb-20 md:mb-24">
          <span ref={labelRef} className="text-[#c9a96e] text-[10px] uppercase tracking-[0.3em] font-semibold" style={{ fontFamily: "'Manrope', sans-serif", opacity: 0 }}>
            Nuestro compromiso
          </span>
          <AnimatedHeadline
            text="La confianza como base de todo"
            data-testid="trust-title"
            className="text-3xl sm:text-4xl md:text-[3.25rem] font-light tracking-tight mt-5 text-[#1a1a1a] leading-[1.15]"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          />
          <div ref={goldLineRef} className="w-12 h-[2px] bg-[#c9a96e] mx-auto mt-7" style={{ transformOrigin: "center center" }} />
        </div>

        {/* Cards */}
        <div ref={cardsRef} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6">
          {differentials.map((item, idx) => {
            const Icon = item.icon;
            return (
              <div
                key={item.title}
                data-testid={`trust-card-${idx}`}
                className="group relative p-8 lg:p-9 bg-white rounded-sm border border-[#e8e4de] service-card hover:border-[#c9a96e]/40 transition-colors duration-300"
                style={{ opacity: 0 }}>
                <div className="absolute top-0 left-0 right-0 h-[2px] bg-[#c9a96e] transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" />
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-sm bg-[#f8f6f3] border border-[#e8e4de] mb-7 group-hover:bg-[#c9a96e]/5 group-hover:border-[#c9a96e]/30 transition-all duration-300">
                  <Icon className="w-5 h-5 text-[#c9a96e] stroke-[1.5]" />
                </div>
                <h3
                  className="text-lg font-medium mb-3 text-[#1a1a1a]"
                  style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "1.35rem" }}>
                  {item.title}
                </h3>
                <p className="text-[13px] text-[#777777] font-light leading-[1.75]" style={{ fontFamily: "'Manrope', sans-serif" }}>
                  {item.desc}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
